import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";

/**
 * ISPM identity list API (POST /ispm/v1/identity).
 * Used to load owner/identity options for policy assignment.
 */
const isMfeMode =
  typeof window !== "undefined" &&
  (window.location.hostname === "loopback" ||
    window.location.pathname.startsWith("/ECMv6") ||
    window.location.pathname.startsWith("/intelligence"));

const identityBasePath = isMfeMode
  ? `${API_BASE_URL}/ispm/v1`
  : `${API_BASE_URL}/api/ispm/v1`;

const identityClient = createAuthenticatedClient(identityBasePath);

export interface IdentityListParams {
  pageSize?: number;
  searchText?: string;
  filters?: unknown[];
}

/** Raw identity item from API (POST /ispm/v1/identity response) */
export interface IdentityItem {
  id?: string;
  name?: string;
  username?: string;
  email?: string;
  status?: string;
  [key: string]: unknown;
}

/** API response: { data: { identities: IdentityItem[], nextPageToken?: string } } */
export interface IdentityListResponse {
  data?: {
    identities?: IdentityItem[];
    nextPageToken?: string;
  };
}

function toLabel(item: IdentityItem): string {
  return item.name ?? item.username ?? item.id ?? "";
}

function toValue(item: IdentityItem): string {
  return item.id ?? item.username ?? item.name ?? "";
}

export interface IdentityOption {
  value: string;
  label: string;
  /** Email shown as sublabel in the dropdown (same as Role's secondaryLabel) */
  secondaryLabel?: string;
}

export const identityApi = {
  /**
   * POST /identity?pageSize=... with body { searchText, filters }.
   * Returns list of options { value, label } for dropdowns.
   */
  getIdentityList: async (
    params: IdentityListParams = {},
  ): Promise<IdentityOption[]> => {
    const { pageSize = 10, searchText = "", filters = [] } = params;
    const query = new URLSearchParams();
    if (pageSize) query.set("pageSize", String(pageSize));

    const url = `/identity?${query.toString()}`;
    const body = { searchText, filters };

    const response = await identityClient.post<IdentityListResponse>(url, body);
    const list = response.data?.identities ?? [];
    return list
      .filter((item) => toValue(item))
      .map((item) => ({
        value: toValue(item),
        label: toLabel(item),
        secondaryLabel: item.email ?? undefined,
      }));
  },
};
